import React, { useState } from 'react';
import { Prompt } from 'react-router';
import { Typography, TextField, InputAdornment, Box, CircularProgress, Theme } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import ContainerBox from 'components/ContainerBox';

import SetPageTitle from 'components/Portals/SetPageTitle';
import BackdropDialog from 'common/components/BackdropDialog';
import SaveButton from 'common/components/SaveButton';

import { api } from 'common/frontend/api';
import { useTranslation } from 'services/localization';

import { useDispatch, useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import * as UserSelectors from 'selectors/UserSelectors';

import {
	getLocationUrlsByUrl,
	validateLocationURL,
	getResevedUrlsForLocation,
	validateBaseURL,
} from './utils';
import { hasAccessToLocation, isAdmin } from 'common/modules/users/utils';
import { getOnlineBaseUrl, getOnlineStoreLocationBaseUrl } from 'common/modules/urls';
import { getClientEnv } from 'common/utils/frontUtils';
import { runTransaction } from 'common/frontend/firebase/firestore';

type FormValues = {
	baseURL: string;
	locationURLs: {
		[locationId: string]: string;
	};
};

const Urls = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const classes = useStyles();
	const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
	const [validatingURL, setValidatingURL] = useState<boolean>(false);

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const rentleStoreUrl = getOnlineBaseUrl(getClientEnv());
	const shopUrls = useSelector(ShopSelectors.activeShopUrls)!;
	const mainLocationId = useSelector(ShopSelectors.activeShopMainLocationId);
	const allShopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const baseURL = useSelector(ShopSelectors.activeShopBaseURL);
	const locationURLsById = useSelector(ShopSelectors.activeShopLocationUrlsById);
	const hasMultipleLocations = allShopLocations.length > 1;
	const activeUserAccess = useSelector(UserSelectors.userActiveShopAccess)!;
	const userRoles = useSelector(UserSelectors.userRoles);
	const hasPrimaryCustomDomain = !!shopUrls?.primaryCustomDomain;
	const form = useForm<FormValues>({
		mode: 'onChange',
		defaultValues: { baseURL, locationURLs: locationURLsById },
	});
	const { isDirty, isSubmitting, isValid, dirtyFields, errors } = form.formState;

	const userIsAdmin = isAdmin(userRoles);

	const onlineStoreLocationBaseUrl = getOnlineStoreLocationBaseUrl({
		shopUrlDoc: shopUrls,
		env: getClientEnv(),
	});

	const handleSubmit = async (data: FormValues) => {
		try {
			if (dirtyFields.baseURL) {
				const oldBaseURL = baseURL;
				await runTransaction(async (transaction) => {
					// Double check that we are not overwriting any existing doc
					const existing = await api(transaction).shopUrls.doc(data.baseURL).get();
					if (!!existing) {
						throw new Error(t('common:validation.urlAlreadyInUse', 'That URL is already in use'));
					}
					// Create new URLs doc
					await api(transaction)
						.shopUrls.doc(data.baseURL)
						.set({
							...shopUrls,
							shopId,
							storeUrl: data.baseURL,
							locationUrls: hasMultipleLocations
								? getLocationUrlsByUrl(data.locationURLs ?? {})
								: getLocationUrlsByUrl({ [mainLocationId]: data.baseURL }),
						});
					//Delete old URLs doc
					await api(transaction).shopUrls.doc(oldBaseURL).delete();
				});
			} else if (dirtyFields.locationURLs) {
				await api()
					.shopUrls.doc(data.baseURL)
					.update({
						locationUrls: getLocationUrlsByUrl(data.locationURLs ?? {}),
					});
			}
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: err.message,
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				priority={3}
				title={t('account.domains.domainsAndUrls', 'Domains & URLs')}
				rightContent={
					<SaveButton
						disabled={!isValid || !isDirty}
						loading={isSubmitting}
						onClick={() => setConfirmDialogOpen(true)}
						t={t}
					/>
				}
			/>
			<Prompt when={false} message={() => t('common:prompts.unsavedChanges')} />
			<Box className={classes.container}>
				<ContainerBox sx={{ order: hasPrimaryCustomDomain ? 2 : 'unset' }}>
					<Typography variant="h6">
						{t('account.domains.rentleUrlEndingTitle', 'Rentle domain path')}
					</Typography>
					<Typography variant="body2">
						{t(
							'account.domains.rentleUrlEndingDescription',
							'Customize the Rentle domain URL ending of your account',
						)}
					</Typography>
					<Box mt={4} display="flex" flexDirection="column">
						<Controller
							control={form.control}
							name="baseURL"
							rules={{
								validate: async (value) => {
									setValidatingURL(true);
									const result = await validateBaseURL(value, baseURL, t);
									setValidatingURL(false);
									return result;
								},
							}}
							render={(renderProps) => (
								<TextField
									value={renderProps.field.value}
									onChange={renderProps.field.onChange}
									variant="outlined"
									disabled={!userIsAdmin}
									helperText={
										!userIsAdmin
											? t('userRoles.onlyEditableByAdmin', 'Only admin users can edit this field')
											: null
									}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">{`${rentleStoreUrl}/`}</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												{validatingURL ? <CircularProgress size={20} /> : null}
											</InputAdornment>
										),
									}}
								/>
							)}
						/>
						<ErrorMessage
							errors={errors}
							name="baseURL"
							render={({ message }) => (
								<Typography variant="caption" color="error">
									{message}
								</Typography>
							)}
						/>
					</Box>
				</ContainerBox>
				{hasMultipleLocations && (
					<ContainerBox>
						<Typography variant="h6">
							{t('account.domains.storeUrlEndingsTitle', 'Store URL endings')}
						</Typography>
						<Typography variant="body2">
							{t(
								'account.domains.storeUrlEndingsDescription',
								'Customize the URL endings for each of your stores',
							)}
						</Typography>
						{allShopLocations.map((location, idx) => (
							<Box key={idx} display="flex" flexDirection="column">
								<Controller
									control={form.control}
									key={location.id}
									name={`locationURLs.${location.id}`}
									rules={{
										validate: (value) =>
											validateLocationURL(
												value,
												getResevedUrlsForLocation(form.getValues().locationURLs, location.id),
												t,
											),
									}}
									render={(renderProps) =>
										hasAccessToLocation(activeUserAccess, location.id) ? (
											<Box mt={4}>
												<TextField
													label={location.name}
													value={renderProps.field.value}
													onChange={renderProps.field.onChange}
													variant="outlined"
													InputProps={{
														startAdornment: (
															<InputAdornment position="start">
																{`${onlineStoreLocationBaseUrl}/`}
															</InputAdornment>
														),
													}}
												/>
											</Box>
										) : (
											<></>
										)
									}
								/>
								<ErrorMessage
									errors={errors}
									name={`locationURLs.${location.id}`}
									render={({ message }) => (
										<Typography variant="caption" color="error">
											{message}
										</Typography>
									)}
								/>
							</Box>
						))}
					</ContainerBox>
				)}
			</Box>
			<BackdropDialog
				label={t('account.domains.confirmUrlChangeTitle', 'Change URLs?')}
				body={
					<Box>
						<Typography variant="body1" gutterBottom>
							{t(
								'account.domains.confirmUrlChangeDescription',
								'Changing your URLs means your current URLs will stop working - they will not be automatically redirected to your new URLs. Remember to update any links you have shared on your website, social media, etc.',
							)}
						</Typography>
					</Box>
				}
				variant="danger"
				isOpen={confirmDialogOpen}
				onClose={() => setConfirmDialogOpen(false)}
				confirm={{
					label: t('common:actions.okIUnderstand', 'OK, I understand'),
					callback: form.handleSubmit(handleSubmit),
					closeAfterDone: true,
				}}
				cancel={{
					label: t('common:actions.cancel'),
					callback: () => setConfirmDialogOpen(false),
				}}
			/>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			display: 'flex',
			flexDirection: 'column',
		},
	}),
);

export default Urls;
